import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  formatBudgetDays,
  listAbsenceBudgets,
  upsertAbsenceBudget,
  type EmployeeAbsenceBudget,
} from "@/lib/employeeAbsences";

interface EmployeeAbsenceBudgetEditorProps {
  userLogin: string;
  userDisplay: string;
  refreshKey?: number;
  onChanged?: () => void;
}

interface BudgetDraft {
  vacation_days: string;
  vacation_carryover_days: string;
  vacation_carryover_manual: boolean;
  note: string;
}

function budgetToDraft(b: EmployeeAbsenceBudget): BudgetDraft {
  const manual = Boolean(b.vacation_carryover_manual);
  return {
    vacation_days: String(b.vacation_days),
    vacation_carryover_days: String(
      manual ? (b.vacation_carryover_days ?? 0) : (b.vacation_carryover_auto_days ?? 0),
    ),
    vacation_carryover_manual: manual,
    note: b.note ?? "",
  };
}

export function EmployeeAbsenceBudgetEditor({
  userLogin,
  userDisplay,
  refreshKey = 0,
  onChanged,
}: EmployeeAbsenceBudgetEditorProps) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [row, setRow] = useState<EmployeeAbsenceBudget | null>(null);
  const [draft, setDraft] = useState<BudgetDraft>({
    vacation_days: "0",
    vacation_carryover_days: "0",
    vacation_carryover_manual: false,
    note: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAbsenceBudgets({ year, user_login: userLogin });
      const found = data[0] ?? null;
      if (found) {
        setRow(found);
        setDraft(budgetToDraft(found));
      } else {
        const empty: EmployeeAbsenceBudget = {
          user_login: userLogin,
          user_display: userDisplay,
          year,
          vacation_days: 0,
          vacation_carryover_days: 0,
          vacation_carryover_manual: false,
          vacation_carryover_auto_days: 0,
          total_vacation_budget: 0,
          child_sick_days: 0,
          overtime_reduction_days: 0,
          special_leave_days: 0,
          used_vacation_days: 0,
          used_child_sick_days: 0,
          used_overtime_reduction_days: 0,
          used_special_leave_days: 0,
          used_sick_days: 0,
          used_unpaid_days: 0,
          used_training_days: 0,
          pending_vacation_days: 0,
          remaining_vacation_days: 0,
          available_vacation_days: 0,
          remaining_child_sick_days: 0,
          remaining_overtime_reduction_days: 0,
          remaining_special_leave_days: 0,
          note: null,
          updated_at: null,
          updated_by_login: null,
        };
        setRow(empty);
        setDraft(budgetToDraft(empty));
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Kontingente konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [year, userLogin, userDisplay]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const manual = draft.vacation_carryover_manual;
      const carryoverRaw = draft.vacation_carryover_days.replace(",", ".").trim();
      const carryoverParsed = carryoverRaw === "" ? 0 : Number(carryoverRaw);
      if (manual && !Number.isFinite(carryoverParsed)) {
        toast.error("Bitte einen gültigen Übertrag eingeben (0 ist erlaubt).");
        setSaving(false);
        return;
      }
      await upsertAbsenceBudget({
        user_login: userLogin,
        user_display: userDisplay,
        year,
        vacation_days: Number(draft.vacation_days.replace(",", ".")) || 0,
        vacation_carryover_manual: manual,
        vacation_carryover_days: manual ? carryoverParsed : null,
        child_sick_days: 0,
        overtime_reduction_days: 0,
        special_leave_days: 0,
        note: draft.note.trim() || undefined,
      });
      toast.success("Urlaubskontingent gespeichert.");
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const autoCarryover = row?.vacation_carryover_auto_days ?? 0;
  const effectiveCarryover = draft.vacation_carryover_manual
    ? Number(draft.vacation_carryover_days.replace(",", ".")) || 0
    : autoCarryover;

  return (
    <div className="space-y-4 rounded-ios border border-separator bg-card p-4 shadow-ios">
      <div>
        <h2 className="text-lg font-semibold text-label">Urlaubskontingent</h2>
        <p className="mt-1 text-sm text-label-secondary">
          Jahresanspruch erneuert sich jedes Jahr. Der Übertrag kommt automatisch aus dem
          Resturlaub des Vorjahres – oder wird manuell gesetzt (auch 0 bei Systemwechsel).
        </p>
      </div>

      <label className="block max-w-xs space-y-1">
        <span className="eh-label">Jahr</span>
        <select
          className="eh-input w-full"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
        >
          {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </label>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="space-y-1">
            <span className="eh-label">Jahresanspruch (Tage)</span>
            <input
              type="text"
              inputMode="decimal"
              className="eh-input w-full"
              value={draft.vacation_days}
              onChange={(e) => setDraft((prev) => ({ ...prev, vacation_days: e.target.value }))}
            />
          </label>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="eh-label">Resturlaub Vorjahr / Übertrag</span>
              <label className="inline-flex items-center gap-2 text-sm text-label">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-accent"
                  checked={draft.vacation_carryover_manual}
                  onChange={(e) => {
                    const manual = e.target.checked;
                    setDraft((prev) => ({
                      ...prev,
                      vacation_carryover_manual: manual,
                      vacation_carryover_days: manual
                        ? prev.vacation_carryover_days || String(autoCarryover)
                        : String(autoCarryover),
                    }));
                  }}
                />
                Manuell festlegen
              </label>
            </div>
            <input
              type="text"
              inputMode="decimal"
              className="eh-input w-full"
              value={
                draft.vacation_carryover_manual
                  ? draft.vacation_carryover_days
                  : formatBudgetDays(autoCarryover)
              }
              disabled={!draft.vacation_carryover_manual}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, vacation_carryover_days: e.target.value }))
              }
            />
            <p className="text-xs text-label-secondary">
              {draft.vacation_carryover_manual
                ? "Manueller Übertrag – 0 Tage ist z. B. bei Systemwechsel möglich."
                : `Automatisch: ${formatBudgetDays(autoCarryover)} Tage Resturlaub aus ${year - 1}.`}
            </p>
          </div>

          <div className="space-y-1 text-sm text-label-secondary md:col-span-2">
            <div>
              Gesamtbudget:{" "}
              <span className="font-medium text-label">
                {formatBudgetDays(
                  (Number(draft.vacation_days.replace(",", ".")) || 0) +
                    (Number.isFinite(effectiveCarryover) ? effectiveCarryover : 0),
                )}{" "}
                Tage
              </span>
              <span className="ml-2 text-xs">
                ({formatBudgetDays(Number(draft.vacation_days.replace(",", ".")) || 0)} Anspruch +{" "}
                {formatBudgetDays(Number.isFinite(effectiveCarryover) ? effectiveCarryover : 0)}{" "}
                Übertrag)
              </span>
            </div>
            <div>
              Genommen (App+Hero): {formatBudgetDays(row?.used_vacation_days ?? 0)} · Beantragt:{" "}
              {formatBudgetDays(row?.pending_vacation_days ?? 0)} · Noch verfügbar:{" "}
              {formatBudgetDays(row?.available_vacation_days ?? row?.remaining_vacation_days ?? 0)}
            </div>
          </div>
          <label className="space-y-1 md:col-span-2">
            <span className="eh-label">Notiz</span>
            <input
              type="text"
              className="eh-input w-full"
              value={draft.note}
              onChange={(e) => setDraft((prev) => ({ ...prev, note: e.target.value }))}
            />
          </label>
        </div>
      )}

      <div className="flex justify-end">
        <Button disabled={loading || saving} onClick={() => void handleSave()}>
          Speichern
        </Button>
      </div>
    </div>
  );
}
